// src/components/PendingExpertAccess.jsx
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabaseClient';
import { Clock, CheckCircle } from 'lucide-react';

export default function PendingExpertAccess({ clientId }) {
  const [pendingExperts, setPendingExperts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sharing, setSharing] = useState(null);


  const loadPendingExperts = useCallback(async () => {
    if (!clientId) return;

    setLoading(true);
    try {
      // Experts qui ont accepté (status='accepted') mais n'ont pas encore d'accès collection
      const { data: expertClients, error } = await supabase
        .from('expert_clients')
        .select('id, expert_id, status, accepted_at, message')
        .eq('client_id', clientId)
        .eq('status', 'accepted')
        .order('accepted_at', { ascending: false });

      if (error) throw error;

      if (!expertClients || expertClients.length === 0) {
        setPendingExperts([]);
        setLoading(false);
        return;
      }

      // Vérifier lesquels n'ont PAS encore d'accès via collection_permissions
      const expertsPending = [];

      for (const ec of expertClients) {
        const { data: perms } = await supabase
          .from('collection_permissions')
          .select('id')
          .eq('client_id', clientId)
          .eq('grantee_id', ec.expert_id)
          .eq('can_view', true)
          .limit(1);

        if (!perms || perms.length === 0) {
          // Pas encore de permission → en attente
          expertsPending.push(ec);
        }
      }

      // Enrichir avec les noms des experts
      if (expertsPending.length > 0) {
        const expertIds = expertsPending.map(e => e.expert_id);
        const { data: profiles } = await supabase
          .rpc('get_profile_basics', { p_user_ids: expertIds });

        const profilesById = {};
        if (profiles) {
          profiles.forEach(p => {
            profilesById[p.id] = p;
          });
        }

        setPendingExperts(
          expertsPending.map(e => ({
            ...e,
            profile: profilesById[e.expert_id] || null
          }))
        );
      } else {
        setPendingExperts([]);
      }
    } catch (err) {
      console.error('loadPendingExperts error:', err);
    } finally {
      setLoading(false);
    }
  }, [clientId]);


  useEffect(() => {
    loadPendingExperts();
  }, [loadPendingExperts]);

  const handleShareWithExpert = async (expertId) => {
    if (!clientId || !expertId) return;

    setSharing(expertId);

    try {
      // Créer la permission collection (can_view = true pour toutes les collections)
      const { error } = await supabase
        .from('collection_permissions')
        .insert({
          client_id: clientId,
          grantee_id: expertId,
          collection_id: null, // null = toutes les collections
          can_view: true,
          can_edit: false
        });

      if (error) throw error;

      alert('✅ Collections partagées avec l\'expert !');
      await loadPendingExperts();
    } catch (err) {
      console.error('handleShareWithExpert error:', err);
      alert(`Erreur: ${err.message}`);
    } finally {
      setSharing(null);
    }
  };

  if (loading) {
    return (
      <div style={{
        padding: '16px',
        background: '#f9fafb',
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        fontSize: '13px',
        color: '#6b7280'
      }}>
        Chargement...
      </div>
    );
  }

  if (pendingExperts.length === 0) {
    return null; // Ne rien afficher si pas d'experts en attente
  }

  return (
    <div style={{
      padding: '20px',
      background: '#fffbeb',
      border: '2px solid #fbbf24',
      borderRadius: '12px',
      marginBottom: '24px'
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        marginBottom: '16px'
      }}>
        <Clock size={24} color="#f59e0b" />
        <div>
          <h3 style={{
            fontSize: '16px',
            fontWeight: 600,
            color: '#92400e',
            margin: 0
          }}>
            {pendingExperts.length} expert{pendingExperts.length > 1 ? 's' : ''} en attente de partage
          </h3>
          <p style={{
            fontSize: '13px',
            color: '#78350f',
            margin: '4px 0 0 0'
          }}>
            Ces experts ont accepté votre invitation et attendent que vous partagiez vos collections
          </p>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {pendingExperts.map(expert => {
          const name = expert.profile
            ? `${expert.profile.first_name || ''} ${expert.profile.last_name || ''}`.trim()
            : 'Expert';

          return (
            <div
              key={expert.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '12px 16px',
                background: '#fff',
                border: '1px solid #fde68a',
                borderRadius: '8px'
              }}
            >
              <div style={{ flex: 1 }}>
                <div style={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#1f2937'
                }}>
                  {name}
                </div>
                {expert.message && (
                  <div style={{
                    fontSize: '12px',
                    color: '#6b7280',
                    marginTop: '4px'
                  }}>
                    Message : {expert.message}
                  </div>
                )}
                <div style={{
                  fontSize: '11px',
                  color: '#9ca3af',
                  marginTop: '4px'
                }}>
                  Accepté le {new Date(expert.accepted_at).toLocaleDateString('fr-FR')}
                </div>
              </div>

              <button
                onClick={() => handleShareWithExpert(expert.expert_id, expert.id)}
                disabled={sharing === expert.expert_id}
                style={{
                  padding: '10px 20px',
                  background: sharing === expert.expert_id ? '#d1d5db' : '#10b981',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: sharing === expert.expert_id ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                {sharing === expert.expert_id ? (
                  'Partage...'
                ) : (
                  <>
                    <CheckCircle size={16} />
                    Partager maintenant
                  </>
                )}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}